'use strict';

const debug = Object.assign({}, console);

/**
 * 2022-06-10
 * Slider v1.0
 */
export class Slider {
    _debug = false;
    _initialized;
    _slider;
    _slider_anchor;
    _slider_active;
    _page;
    _page_anchor;
    _page_active;
    _duration;
    _direction;
    _delay;
    _status = 'default';
    _left;
    _start;
    #latest = {};

    #allow_status = [
        'default',
        'ready',
        'play',
        'move',
        'scroll',
        'go',
        'terminate',
    ];

    #max;

    /**
     * control auto play
     */
    #autoPlay;

    #playProc;

    constructor({slider, slider_anchor, slider_active, page, page_anchor, page_active, direction, duration, delay, autoPlay}) {
        if (!this._debug) debug.group = debug.log = debug.groupEnd = () => {};

        this.slider = slider;
        this.slider_anchor = slider_anchor;
        this.slider_active = slider_active;
        this.page = page;
        this.page_anchor = page_anchor;
        this.page_active = page_active;
        this.direction = direction;
        this.duration = duration;
        this.delay = delay;
        this.#autoPlay = autoPlay ?? false;
        this.timestamp = new Date();

        return this.initialize();
    }

    initialize() {
        // define events
        if (!this.initialized) {
            this.initialized = true;

            // define event
            this.down = this.down.bind(this);
            this.up = this.up.bind(this);
            this.move = this.move.bind(this);
            this.enter = this.enter.bind(this);
            this.leave = this.leave.bind(this);
            this.scroll = this.scroll.bind(this);
            this.click = this.click.bind(this);
            this.pageClick = this.pageClick.bind(this);

            // add events
            this.slider.addEventListener('mouseenter', this.enter);
            this.slider.addEventListener('mousedown', this.down);
            this.slider.addEventListener('mousemove', this.move);
            this.slider.addEventListener('mouseup', this.up);
            this.slider.addEventListener('mouseleave', this.leave);
            this.slider.addEventListener('scroll', this.scroll);
            this.slider.addEventListener('click', this.click);
            this.page.addEventListener('click', this.pageClick);

            // init page
            let i;
            for (i = 0; i < this.page.children.length; i++) {
                let item = this.page.children[i];
                item.dataset.page = String(i);
            }
            this.#max = this.page.children.length;

            this.status = 'ready';

            if (this.#autoPlay) this.play();

            return true;
        } else {
            return false;
        }
    }

    async destroy() {
        // clearInterval, clearTimeout
        this.clear();

        this.slider.removeEventListener('mouseenter', this.enter);
        this.slider.removeEventListener('mousedown', this.down);
        this.slider.removeEventListener('mousemove', this.move);
        this.slider.removeEventListener('mouseup', this.up);
        this.slider.removeEventListener('mouseleave', this.leave);
        this.slider.removeEventListener('scroll', this.scroll);
        this.slider.removeEventListener('click', this.click);
        this.page.removeEventListener('click', this.pageClick);

        delete this._slider;
        delete this._slider_active;
        delete this._page;
        delete this._page_active;
        delete this._duration;
        delete this._delay;
        delete this._status;
        delete this.timestamp;

        return true;
    }

    /**
     * clearInterval, clearTimeout
     */
    clear() {
        clearInterval(this.#playProc);
        clearTimeout(this.#playProc);
        clearTimeout(this.#scrollProc);
    }

    play() {
        this.clear();
        if (this.#autoPlay) {
            this.#playProc = setInterval(async () => {
                debug.group('[PLAY] setInterval');
                debug.log('autoPlay:', this.#autoPlay, 'direction:', this.direction, 'page:', this.getPage);
                await this.next();
                debug.groupEnd();
                return true;
            }, this.delay + this.duration);
        } else {
            this.#playProc = setTimeout(async () => {
                debug.group('[PLAY] setTimeout');
                debug.log('[PLAY] setTimeout', 'autoPlay:', this.#autoPlay, 'direction:', this.direction, 'page:', this.getPage);
                await this.next();
                debug.groupEnd();
                return true;
            }, this.delay + this.duration);
        }
    }

    down(e) {
        this.clear();
        this.status = 'move';
        e.preventDefault();
        this.start = e.clientX;
        this.left = this.slider.scrollLeft;
        this.slider.dataset.move = 'Y';
        this.#latest.down = {
            'clientX'  : e.clientX,
            'clientY'  : e.clientY,
            'timeStamp': e.timeStamp
        }
        debug.log('%c[EVENT] down', 'font-size: 14px; color:tomato;', this.#latest);
        // return false;
        return true;
    }

    downStart(e) {

    }

    move(e) {
        if (this.status === 'move' && e.buttons && Math.abs(this.start - e.clientX) > 10) {
            this.slider.scrollLeft = this.left + (this.start - e.clientX);
            debug.log('%c[EVENT] move', 'font-size: 14px; color:tomato;');
            return true;
        } else {
            return false;
        }
    }

    up(e) {
        this.#latest.up = {
            'clientX'  : e.clientX,
            'clientY'  : e.clientY,
            'timeStamp': e.timeStamp
        }
        debug.log('%c[EVENT] up', 'font-size: 14px; color:tomato;', this.#latest);
        return this.end(e);
    }

    enter(e) {
        debug.log('%c[EVENT] enter', 'font-size: 14px; color:tomato;');
        return true;
    }

    leave(e) {
        debug.log('%c[EVENT] leave', 'font-size: 14px; color:tomato;');
        return this.end(e);
    }

    end() {
        if (this.status === 'move') {
            let page = this.getPage;
            let old_page = this.slider.dataset.page;
            this.go(page);
            debug.log('[END] move', this.status, page, old_page);
        } else if (this.status === 'scroll') {
            let page = this.getPage;
            let old_page = this.slider.dataset.page;
            this.go(page);
            debug.log('[END] scroll', this.status, page, old_page);
        }
    }

    click(e) {
        this.#latest.click = {
            'clientX'  : e.clientX,
            'clientY'  : e.clientY,
            'timeStamp': e.timeStamp
        }

        let min_x = Math.min(this.#latest.down.clientX, this.#latest.up.clientX, this.#latest.click.clientX);
        let min_y = Math.min(this.#latest.down.clientY, this.#latest.up.clientY, this.#latest.click.clientY);
        let min_t = Math.min(this.#latest.down.timeStamp, this.#latest.up.timeStamp, this.#latest.click.timeStamp);

        let max_x = Math.max(this.#latest.down.clientX, this.#latest.up.clientX, this.#latest.click.clientX);
        let max_y = Math.max(this.#latest.down.clientY, this.#latest.up.clientY, this.#latest.click.clientY);
        let max_t = Math.max(this.#latest.down.timeStamp, this.#latest.up.timeStamp, this.#latest.click.timeStamp);

        let diff_x = (max_x - min_x) / this.slider.clientWidth * 100;
        let diff_y = (max_y - min_y) / this.slider.clientHeight * 100;
        let diff_t = max_t - min_t;

        debug.log('%c[EVENT] click', 'font-size: 14px; color:tomato;', {diff_x, diff_y, diff_t}, this.#latest);
        this.#latest = {};

        if (diff_x > 10 || diff_t > 300) {
            e.preventDefault();
            return this.end(e);
        }
    }

    #scrollProc;
    scroll(e) {
        // if (this.status === 'play' || this.status === 'ready') {
        if (this.status === 'ready' || this.status === 'scroll') {
            if (this.status !== 'scroll') this.status = 'scroll';
            this.clear();
            this.#scrollProc = setTimeout(() => {
                let page = this.getPage;
                let page_old = this.slider.dataset.page;
                debug.log('[SCROLL] call end', 'getPage:', page, 'oldPage:', page_old);
                if (Number(page) !== Number(page_old)) {
                    debug.log('[SCROLL] set page');
                    this.setPage(page);
                }
                this.status = 'ready';
                this.slider.dataset.move = 'N';
                this.#autoPlay && this.play();
                // this.end(e);
            }, 150);
        }
    }

    pageClick(e) {
        e.preventDefault();
        debug.log('%c[EVENT] PageClick', 'font-size: 14px; color:tomato;');
        let page = e.target.dataset.page ?? e.target.parentNode.dataset.page;
        return this.go(page);
    }

    get getPage() {
        // 현재 스크롤 위치
        let scroll_x = this.slider.scrollLeft;
        // 시작 스크롤 위치
        let start = this.left ?? this.slider.scrollLeft;
        // 스크롤 이동 값
        let move = scroll_x - start;
        // 페이지 가로 크기
        let page_width = this.slider.clientWidth;
        let progress = move / page_width;
        let page = scroll_x / page_width;

        let is_control = (this.status === 'scroll' || this.status === 'move');
        if (0.1 <= progress) {
            if (is_control) this.direction = 'right';
            page = Math.ceil(page);
        } else if (progress <= -0.1) {
            if (is_control) this.direction = 'left';
            page = Math.floor(page);
        } else {
            page = Math.round(page);
        }

        // debug.log('[GET_PAGE]', this.status, this.direction, {move, page, progress});
        return page;
    }

    setPage(page) {
        let page_on = this.page_active;
        for (let i = 0; i < this.page.children.length; i++) {
            let el = this.page.children[i];
            el.classList.contains(page_on) && el.classList.remove(page_on);
        }
        this.page.children[page].classList.add(page_on);

        this.slider.dataset.page = page;
        this.page.dataset.page = page;

        debug.log('[SET_PAGE]', page, this.direction);
    }

    async go(page) {
        if (page >= 0) {
            debug.log('[GO]', page, 'direction:', this.direction);
            this.status = 'play';
            this.setPage(page);
            return this.scrollMove(this.slider.clientWidth * page);
        } else {
            return true;
        }
    }

    async next() {
        let direction = this.direction === 'left' ? -1 : 1;
        let page = this.getPage + direction;
        if (this.#max <= page) {
            page = 0;
        } else if (0 > page) {
            page = this.#max - 1;
        }
        debug.group('[NEXT]', this.getPage, page, 'oldPage:', this.slider.dataset.page, 'direction:', this.direction);

        debug.log('[NEXT]', 'call to go');
        await this.go(page);
        debug.log('[NEXT]', 'complete');
        debug.groupEnd();

        return true;
    }

    #requestID;
    /**
     * Scroll from initY to 0
     * @param {number} x - initial scroll X
     * @param {string} timingName - timing function name. Can be one of linear, ease-in, ease-out, ease-in-out
     */
    scrollMove(x, timingName = 'ease-out') {
        return new Promise(async resolve => {
            debug.group('[SCROLL_MOVE]');
            clearTimeout(this.#scrollProc);

            const timingFunc = this.timingFunc[timingName];
            let start = null;
            let initX = this.slider.scrollLeft;
            let moveX = x - initX;
            let interval = 50;
            let duration = this.duration - interval;
            this.slider.dataset.move = 'Y';

            const step = async timestamp => {
                window.cancelAnimationFrame(this.#requestID);
                start = start || timestamp;
                let progress = timestamp - start;
                if (this.status === 'play' && progress < duration) {
                    let time = Math.min(1, ((timestamp - start) / duration));
                    let left = initX + (timingFunc(time) * moveX);
                    this.slider.scrollLeft = left;
                    debug.log('RUN', 'time:', time, Math.round(left * 100) / 100, 'status:', this.status, 'direction:', this.direction);
                    this.#requestID = window.requestAnimationFrame(await step);
                } else {
                    this.slider.scrollLeft = x;
                    setTimeout(() => {
                        debug.log('RUN', 'finish');
                        if (this.status === 'play') this.status = 'ready';
                        this.slider.dataset.move = 'N';
                        debug.groupEnd();
                        return resolve(true);
                    }, interval);
                }
            };

            this.#requestID = window.requestAnimationFrame(await step);
        });
    }

    get timingFunc() {
        return {
            "linear": t => t,
            "ease-in": t => t * t,
            "ease-out": t => t * (2 - t),
            "ease-in-out": t => (t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t)
        }
    }

    /**
     * slider document element
     */
    get initialized() {
        return this._initialized;
    }

    set initialized(boolean) {
        this._initialized = boolean;
    }

    /**
     * slider document element
     */
    get slider() {
        return this._slider;
    }

    set slider(element) {
        if (element === null) throw new Error('Slider element is not defined.');
        this._slider = element;
    }

    /**
     * slider anchor class name
     */
    get slider_anchor() {
        return this._slider_anchor;
    }

    set slider_anchor(class_name) {
        this._slider_anchor = class_name ?? '';
    }

    /**
     * active slider class name
     */
    get slider_active() {
        return this._slider_active;
    }

    set slider_active(class_name) {
        this._slider_active = class_name ?? 'on';
    }

    /**
     * slider page document element
     */
    get page() {
        return this._page;
    }

    set page(element) {
        if (element === null) throw new Error('Page element is not defined.');
        this._page = element;
    }

    /**
     * page anchor class name
     */
    get page_anchor() {
        return this._page_anchor;
    }

    set page_anchor(class_name) {
        this._page_anchor = class_name ?? '';
    }

    /**
     * page active class name
     */
    get page_active() {
        return this._page_active;
    }

    set page_active(class_name) {
        this._page_active = class_name ?? 'on';
    }

    /**
     * slider direction
     */
    get direction() {
        return this._direction;
    }

    set direction(value) {
        this._direction = value === 'left' ? 'left' : 'right';
    }

    /**
     * slider duration
     */
    get duration() {
        return this._duration;
    }

    set duration(number) {
        this._duration = typeof(number) === 'Number' && number >= 100 ? number : 300;
    }

    /**
     * slider delay
     */
    get delay() {
        return this._delay;
    }

    set delay(number) {
        this._delay = typeof(number) === 'Number' && number >= this.duration ? number : 3000;
    }

    get status() {
        return this._status;
    }

    set status(value) {
        if (this.#allow_status.find(status => status === value) === false) throw new Error('This status is not allowed.');
        debug.log('[SET STATUS]', this._status, '->', value, this.direction);
        this._status = value;
    }

    /**
     * scroll left
     */
    get left() {
        return this._left;
    }

    set left(value) {
        this._left = value;
    }

    /**
     * control start position
     */
    get start() {
        return this._start;
    }

    set start(value) {
        this._start = value;
    }
}

